// Date-range logic for the scorecard date pickers, shared by all five Driver
// Manager surfaces (dashboard, roster list, individual, compare, risk matrix).
//
// This used to be copy-pasted into each of them, and had drifted four ways: the
// risk matrix skipped the 7th-of-month settle cutover, compare defaulted to one
// month instead of six and offered a Clear button the others did not, and
// compare and the risk matrix never snapped a picked date to a boundary at all.
//
// It is deliberately expressed in terms of the reporting WINDOW rather than the
// calendar month. Scorecards are moving from monthly records to sub-month
// periods (FI-2140), so "the last settled window" and "snap to a boundary" are
// the stable ideas; which boundary that is changes in here, once, rather than
// at five call sites.
import moment from 'moment'

import { justDate } from '../helpers/dates'
import { DTPICKER_WIDGET_PARENT } from '../mixins/dtpicker_portal_mixin'

export const DATE_FMT = 'MM-DD-YYYY'

// the quick-range chips beside the pickers
export const QUICK_RANGE_MONTHS = [1, 3, 6, 12]

// the chip every surface opens on: the last six published periods
export const DEFAULT_QUICK_RANGE_MONTHS = 1

// matches the backend's own cap (scorecard_periods.MAX_RANGE_DAYS); a wider
// range is rejected there, so the picker must not produce one. It is counted in
// days, not calendar months: a rolling year ending mid-month touches thirteen
// months, and the month-counted cap it replaced refused exactly the 12m chip.
export const MAX_RANGE_DAYS = 366

// how far back the pickers let you reach
export const PICKER_YEARS_BACK = 3

// days a period is given to settle before it is published.
// MUST MATCH scorecard_periods.SETTLE_DAYS.
export const SETTLE_DAYS = 5


// ── period boundaries ──────────────────────────────────────────────────────
//
// Scorecards are stored as six sub-month periods from the cutover month onward,
// and as one record per calendar month before it. The pickers offer exactly the
// boundaries of those stored records: every period start as a "from", every
// period end as a "to". That is the point of the whole migration -- a customer
// can ask for the 11th to the 15th and get the 11th to the 15th -- but only on
// boundaries, because a range landing mid-period cannot be answered from stored
// records without recomputing it.
//
// MUST MATCH reports/scorecard_periods.py. Both repos read the same table, so a
// picker offering a boundary the backend does not store returns an empty window.
export const PERIOD_START_DAYS = [1, 6, 11, 16, 21, 26]
export const PERIOD_CUTOVER_MONTH = '2026-08'

// Is this month stored as six periods, or as one monthly record?
export function isPeriodMonth(m) {
  return m.format('YYYY-MM') >= PERIOD_CUTOVER_MONTH
}

// The [start, end] day-of-month pairs a month is stored as. One pair before the
// cutover; six after, the last running to end-of-month (so 3-6 days, never
// assume 5).
export function periodBoundsForMonth(m) {
  const eom = m.clone().endOf('month').date()
  if (!isPeriodMonth(m)) return [[1, eom]]
  return PERIOD_START_DAYS.map((first, i) => [
    first,
    i + 1 < PERIOD_START_DAYS.length ? PERIOD_START_DAYS[i + 1] - 1 : eom,
  ])
}

function _boundaryDates(minD, maxD, pick) {
  const out = []
  const last = maxD.clone().startOf('month')
  for (let m = minD.clone().startOf('month'); m.isSameOrBefore(last, 'month'); m.add(1, 'month')) {
    periodBoundsForMonth(m).forEach(bounds => {
      const d = m.clone().date(pick(bounds))
      if (d.isSameOrAfter(minD, 'day') && d.isSameOrBefore(maxD, 'day')) out.push(d.clone())
    })
  }
  return out
}

// Selectable "from" dates: every period start.
export function buildPeriodStartDates(minD, maxD) {
  return _boundaryDates(minD, maxD, ([start]) => start)
}

// Selectable "to" dates: every period end up to maxD.
//
// Callers pass the last published period end as maxD, because nothing later is
// ever returned. This used to add "today" so the unpublished tail could be
// reached and computed on the fly; FI-2229 removed on-the-fly computation, and
// the picker stopped offering a day no read answers for.
export function buildPeriodEndDates(minD, maxD) {
  return _boundaryDates(minD, maxD, ([, end]) => end)
}

// Snap a date back onto the boundary of the period containing it.
export function snapToPeriodStart(d) {
  const bounds = periodBoundsForMonth(d)
  const day = d.date()
  const [start] = bounds.filter(([s]) => s <= day).pop()
  return d.clone().date(start)
}

export function snapToPeriodEnd(d) {
  const bounds = periodBoundsForMonth(d)
  const day = d.date()
  const [, end] = bounds.find(([, e]) => day <= e) || bounds[bounds.length - 1]
  return d.clone().date(end)
}

// Snap a date FORWARD to the first period start on or after it -- itself when it
// already starts one. Before the cutover that is the 1st of the next month.
export function nextPeriodStart(d) {
  const start = snapToPeriodStart(d)
  return start.isSame(d, 'day') ? start : snapToPeriodEnd(d).add(1, 'day')
}

// Snap a date BACK to the last period end on or before it -- itself when it
// already ends one.
export function previousPeriodEnd(d) {
  const end = snapToPeriodEnd(d)
  return end.isSame(d, 'day') ? end : snapToPeriodStart(d).subtract(1, 'day')
}


// Label for one point on the trends x-axis.
//
// The API sends the record-key suffix: 'YYYY-MM' for a legacy monthly record,
// 'YYYY-MM-DD' for a period. Two things have to be true of the result.
//
// It has to distinguish the six periods of a month, which formatting both as
// 'YYYY-MM' did not -- all six rendered as one label.
//
// And a month must not be mistakable for a day. 'MMM YY' renders August 2026 as
// "Aug 26", which is also exactly how the Aug 26-31 period reads as 'MMM D':
// the same string on the same axis meaning two different things. The year gets
// an apostrophe and the period gets its full span, so neither can be read as
// the other.
//
// `span` is the { start, end } the API reports for the point (FI-2229). A month
// point can cover only part of its month -- the range starts or ends inside it,
// or its tail has not published -- and then it is labelled with the days it
// holds, so 20 days of September does not pass for the whole month. Month and
// period points never share an axis, so "Sep 1-20" cannot be taken for a period.
export function trendPointLabel(suffix, span = {}) {
  const s = String(suffix || '')
  if (s.length === 10) {
    const d = moment(s, 'YYYY-MM-DD')
    if (!d.isValid()) return s
    const bounds = periodBoundsForMonth(d)
    const match = bounds.find(([from, to]) => d.date() >= from && d.date() <= to)
    // en dash, and the month is not repeated: "Aug 11-15"
    return match ? `${d.format('MMM D')}\u2013${match[1]}` : d.format('MMM D')
  }
  const m = moment(s, 'YYYY-MM')
  if (!m.isValid()) return s
  const start = moment(span.start, 'YYYY-MM-DD', true)
  const end = moment(span.end, 'YYYY-MM-DD', true)
  const partial = start.isValid() && end.isValid()
    && (start.date() !== 1 || !end.isSame(end.clone().endOf('month'), 'day'))
  return partial ? `${start.format('MMM D')}\u2013${end.date()}` : m.format("MMM 'YY")
}

// End date of the most recent period whose settle window has closed.
//
// Mirrors scorecard_periods.last_published_period_end, and verified against it
// day by day: the two agree on all 303 dates after the cutover. They diverge
// before it, because the Python walks in periods regardless of era while this
// respects the cutover and treats a pre-cutover month as one record. That region
// is never asked about -- callers pass today, and today is permanently after the
// cutover -- and neither answer is meaningful there, since a pre-cutover month
// was published as a whole on the 7th of the following month.
export function lastPublishedPeriodEnd(now = moment()) {
  const today = now.clone().startOf('day')
  let day = today.clone()
  for (let i = 0; i < 400; i += 1) {
    const bounds = periodBoundsForMonth(day)
    const [start, end] = bounds.find(([s, e]) => day.date() >= s && day.date() <= e) || bounds[0]
    const periodEnd = day.clone().date(end)
    // against TODAY, not against the day the walk has reached -- comparing the
    // walked-back day to its own period end is always 0 and never settles
    if (today.diff(periodEnd, 'days') >= SETTLE_DAYS) return periodEnd
    day = day.clone().date(start).subtract(1, 'day')
  }
  return today
}

// The window a quick-range chip selects: `months` back from the last published
// period end.
//
// It used to end at the last complete calendar month, held back to the 7th of
// the next -- so late in September the pickers still ended on Aug 31, three
// weeks of published periods behind (FI-2229). It ends on the last published
// period now, which makes 1m exactly the last six periods.
//
// The start is snapped FORWARD onto a stored boundary. Before the cutover a
// month is one record, so a window reaching back to Jun 21 would come back as
// all of June: more than the chip says, and for 12m more than MAX_RANGE_DAYS.
// Snapping forward to Jul 1 gives a little less instead, and the pickers show
// exactly that. Inside the period era every start is already a boundary.
export function quickRangeDates(months, now = moment()) {
  const to = lastPublishedPeriodEnd(now)
  const from = nextPeriodStart(to.clone().subtract(months, 'months').add(1, 'day'))
  return { from: justDate(from), to: justDate(to) }
}

// The default reporting window shared by every scorecard surface and the
// Coaching Portal's flagged-drivers panel. The two compute flags over the SAME
// window on purpose — they must agree on who is flagged (see
// utils/scorecard_flags.js); a portal reading a different period than the
// dashboard's Flagged tab would disagree with it every period.
export function defaultScorecardRange(now = moment()) {
  const { from, to } = quickRangeDates(DEFAULT_QUICK_RANGE_MONTHS, now)
  return { from: moment(from, DATE_FMT), to: moment(to, DATE_FMT) }
}

// Which chip (if any) the current range corresponds to, so it can render active.
export function activeQuickRangeMonths({ from, to }, now = moment()) {
  for (const months of QUICK_RANGE_MONTHS) {
    const range = quickRangeDates(months, now)
    if (range.from === from && range.to === to) return months
  }
  return null
}

// Config for one of the two datetimepickers. `which` is 'from' or 'to'.
//
// The pickers offer the boundaries of the stored records and nothing else: every
// period start as a "from", every period end as a "to", ending at the last
// published period -- the latest day any read returns data for. A date between boundaries would be answered with
// the whole record around it, so the picker would show one range while the page
// read another; offering only boundaries is what keeps the two the same.
export function scorecardPickerOptions(which, now = moment()) {
  const minD = now.clone().subtract(PICKER_YEARS_BACK, 'years').startOf('month')
  const maxD = lastPublishedPeriodEnd(now)
  return {
    format: DATE_FMT,
    useCurrent: false,
    widgetParent: DTPICKER_WIDGET_PARENT,
    widgetPositioning: { horizontal: 'left', vertical: 'bottom' },
    minDate: minD,
    maxDate: maxD,
    enabledDates: which === 'from'
      ? buildPeriodStartDates(minD, maxD)
      : buildPeriodEndDates(minD, maxD),
    keepOpen: false,
    // the range is required, so there is nothing sensible to clear it to
    showClear: false,
    showClose: true,
  }
}

// Days a range covers, inclusive of both ends. The unit the API caps on
// (scorecard_periods.MAX_RANGE_DAYS).
export function rangeDays(from, to) {
  return to.clone().startOf('day').diff(from.clone().startOf('day'), 'days') + 1
}

// Correct a picked range so it is exactly the range the page will return data
// for: clamp it to the last published period, snap both ends onto the stored
// boundaries, un-invert it, and cap its length.
//
// `changed` names the end the user just touched, and it drives everything:
//
//   - every correction — un-inverting, capping the span — moves the UNtouched
//     end. Moving the date someone just picked is how a picker feels like it is
//     fighting you.
//
// Dates snap to PERIOD boundaries, not month boundaries: a "from" to the start
// of the period containing it, a "to" to that period's end. The pickers only
// offer boundaries, so this catches deep links and values that were valid under
// the old rules. Both ends snap, whichever was touched, because the read path
// answers a mid-period bound with the whole period either way.
//
// A capped end moves INWARD onto a boundary -- a "from" forward, a "to" back --
// so the corrected range never exceeds MAX_RANGE_DAYS, before or after the
// cutover.
//
// Returns the corrected { from, to } as MM-DD-YYYY strings, or NULL when either
// end is unparseable — a half-typed or cleared field. Null means "do nothing":
// do not correct the value out from under the person typing it, and do not
// reload. Each component's watcher used to own that bail as
// `if (!to.isValid()) return`, which also skipped its reload; returning a range
// here instead let "Invalid date" through to the API as a month string.
export function normalizeRange({ from, to }, { changed = 'to', now = moment() } = {}) {
  const latest = lastPublishedPeriodEnd(now)
  let f = moment(from, DATE_FMT)
  let t = moment(to, DATE_FMT)
  if (!f.isValid() || !t.isValid()) return null

  // A "to" on its period end, or on the last published period, whichever comes
  // first. Applied whichever end was touched: the old watchers only clamped
  // inside the "to" handler and relied on the "from" handler cascading into it,
  // which does not happen when `to` is not reassigned.
  const fitEnd = d => moment.min(snapToPeriodEnd(d), latest)

  t = fitEnd(t)
  f = snapToPeriodStart(f)

  if (changed === 'from') {
    // the END OF THAT PERIOD, not the end of its month: picking a "from" of
    // Aug 11 against a stale "to" of Aug 5 should give Aug 15, not Aug 31
    if (f.isAfter(t, 'day')) t = fitEnd(f)
    if (rangeDays(f, t) > MAX_RANGE_DAYS) {
      t = moment.min(previousPeriodEnd(f.clone().add(MAX_RANGE_DAYS - 1, 'days')), latest)
    }
    // `to` can never be later than the last published period, so a `from` past
    // it — only reachable by deep link, since the pickers stop there — has to
    // come back
    if (f.isAfter(t, 'day')) f = snapToPeriodStart(t)
  } else {
    if (f.isAfter(t, 'day')) f = snapToPeriodStart(t)
    if (rangeDays(f, t) > MAX_RANGE_DAYS) {
      f = nextPeriodStart(t.clone().subtract(MAX_RANGE_DAYS - 1, 'days'))
    }
  }

  return { from: justDate(f), to: justDate(t) }
}
